
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { PlatformConfig } from "../config/platformConfig";
import { PlatformState } from "./types";
import { Loader2, CheckCircle, XCircle, AlertCircle } from "lucide-react";

interface ApiPlatformFormProps {
  platform: PlatformConfig;
  platformState: PlatformState;
  onInputChange: (platformId: string, fieldName: string, value: string) => void;
  onSaveCredentials: (platformId: string) => void;
  onTestConnection: (platformId: string) => void;
  isSubmitting: Record<string, boolean>;
  isFocused?: boolean;
}

export const ApiPlatformForm: React.FC<ApiPlatformFormProps> = ({
  platform,
  platformState,
  onInputChange,
  onSaveCredentials,
  onTestConnection,
  isSubmitting,
  isFocused = false
}) => {
  const getStatusIcon = () => {
    switch (platformState.status) {
      case 'validating':
        return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
      case 'valid':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'invalid':
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (platformState.status) {
      case 'validating':
        return 'Testing connection...';
      case 'valid':
        return 'Connected';
      case 'invalid':
        return 'Invalid credentials';
      case 'error':
        return 'Error occurred';
      default:
        return 'Not configured';
    }
  };

  return (
    <Card className={`transition-all ${isFocused ? 'ring-2 ring-blue-500' : ''}`}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center space-x-3">
          <platform.icon className="h-6 w-6" />
          <div>
            <CardTitle className="text-lg">{platform.name}</CardTitle>
            <CardDescription>{platform.description}</CardDescription>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <Badge variant={platformState.status === 'valid' ? 'default' : 'secondary'}>
            {getStatusText()}
          </Badge>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${platform.id}-enabled`}>Enable {platform.name}</Label>
          <Switch
            id={`${platform.id}-enabled`}
            checked={platformState.enabled}
            onCheckedChange={(checked) => onInputChange(platform.id, 'enabled', checked.toString())}
          />
        </div>

        {platform.fields.map((field) => (
          <div key={field.name} className="space-y-2">
            <Label htmlFor={`${platform.id}-${field.name}`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              id={`${platform.id}-${field.name}`}
              type={field.type}
              placeholder={field.placeholder}
              value={platformState.credentials[field.name] || ''}
              onChange={(e) => onInputChange(platform.id, field.name, e.target.value)}
            />
            {field.description && (
              <p className="text-sm text-gray-500">{field.description}</p>
            )}
          </div>
        ))}

        {platformState.message && (
          <div className={`p-3 rounded-md text-sm ${
            platformState.status === 'valid' ? 'bg-green-50 text-green-700' : 
            platformState.status === 'error' || platformState.status === 'invalid' ? 'bg-red-50 text-red-700' : 
            'bg-blue-50 text-blue-700'
          }`}>
            {platformState.message}
          </div>
        )}

        <div className="flex space-x-2 pt-4">
          <Button
            onClick={() => onSaveCredentials(platform.id)}
            disabled={isSubmitting[platform.id]}
            className="flex-1"
          >
            {isSubmitting[platform.id] && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Credentials
          </Button>
          <Button
            variant="outline"
            onClick={() => onTestConnection(platform.id)}
            disabled={isSubmitting[platform.id] || !Object.values(platformState.credentials).some(v => v && v !== '••••••••••••••••')}
          >
            Test Connection
          </Button>
        </div>

        {platform.documentationUrl && (
          <div className="pt-2">
            <a
              href={platform.documentationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              View API documentation →
            </a>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
